import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { ChevronDown, Check } from 'lucide-react'
import RoomTypeTreeRow from './RoomTypeTreeRow'
import HotelSectionHeader from '@/components/groups/HotelSectionHeader'

interface SingleSelectOption {
  value: string
  label: string
  /** Optional room-type hierarchy metadata — enables the parent/child tree UI. */
  depth?: number
  isParent?: boolean
  childCount?: number
  isLastChild?: boolean
  spacedAbove?: boolean
  /** Hairline separator above this option (e.g. between "All Property" and rooms). */
  dividerBefore?: boolean
  /** Backend says this room isn't being priced — renders a small muted marker. */
  notPriced?: boolean
  /** Groups: which hotel this room belongs to, and whether that hotel leads the group. */
  hotelName?: string | null
  hotelPropertyId?: number | null
  isLeadHotel?: boolean
  /** Groups: the first room of a new hotel's block — draws a heading above it. */
  startsHotelBlock?: boolean
  /** Groups: this room follows a parent in another hotel, so the link is stated, not drawn. */
  followsName?: string | null
  followsPropertyName?: string | null
}

function hasTreeMeta(o: SingleSelectOption): boolean {
  return o.depth !== undefined || o.isParent !== undefined || o.childCount !== undefined
}

interface SingleSelectDropdownProps {
  label: string
  options: SingleSelectOption[]
  value: string
  onChange: (value: string) => void
}

export default function SingleSelectDropdown({
  label,
  options,
  value,
  onChange,
}: SingleSelectDropdownProps) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 4, left: rect.left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePos()
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
          open
            ? 'border-primary/30 bg-primary/5 text-primary'
            : 'border-border/60 bg-white text-muted-foreground hover:text-foreground hover:border-border'
        )}
      >
        <span className="uppercase tracking-wider">{label}</span>
        <span className="font-semibold text-foreground">{selectedLabel}</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] min-w-[200px] rounded-lg border border-border/60 bg-white p-1.5 shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="max-h-[280px] overflow-y-auto">
            {options.map((option) => {
              const selected = option.value === value
              const tree = hasTreeMeta(option)
              return (
                <React.Fragment key={option.value}>
                  {option.dividerBefore && (
                    <div className="my-1 border-t border-border/40" />
                  )}
                  {/* A hotel heading, so a unit-wide list reads as blocks. Absent unless the list
                      actually spans hotels. A HEADING, not a chip: the capped chip clipped
                      "Modern Studios Close To Hyde Park" to "…Close To Hyd…" in a panel with room
                      to spare, which is the one place the name has to be readable in full. */}
                  {option.startsHotelBlock && option.hotelName && (
                    <div className="mt-1.5 border-t border-border/40 px-2 pb-1 pt-1.5">
                      <HotelSectionHeader
                        name={option.hotelName}
                        propertyId={option.hotelPropertyId}
                        isLead={option.isLeadHotel}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      onChange(option.value)
                      setOpen(false)
                    }}
                    className={cn(
                      'group flex w-full items-stretch gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                      option.spacedAbove && 'mt-1',
                      selected
                        ? 'bg-muted/50 text-foreground'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    <span className="flex items-center">
                      <span className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                        selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border'
                      )}>
                        {selected && <Check className="h-3 w-3" />}
                      </span>
                    </span>
                    {tree ? (
                      <RoomTypeTreeRow
                        name={option.label}
                        depth={option.depth}
                        isParent={option.isParent}
                        childCount={option.childCount}
                        isLastChild={option.isLastChild}
                        selected={selected}
                        followsName={option.followsName}
                        followsPropertyName={option.followsPropertyName}
                      />
                    ) : (
                      <span className="flex min-w-0 flex-1 items-center">
                        <span className="truncate">{option.label}</span>
                      </span>
                    )}
                    {option.notPriced && (
                      <span className="shrink-0 self-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/80">
                        Not priced
                      </span>
                    )}
                  </button>
                </React.Fragment>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
